import * as Phaser from 'phaser';
import {
  resolveColor,
  routeOutline,
  UNSELECTED_ROUTE_ALPHA,
  type MapDocument,
  type Vec2,
} from '../../sim/map/index.ts';

/**
 * A second camera looking at the whole map, parked in a corner of the canvas. It renders the same
 * Game Objects as the main view rather than a redrawn copy, so land and water can never
 * drift out of sync with the editor — the only bookkeeping is an ignore list for the chrome that
 * makes no sense at map scale (grid, handles, labels, marquee).
 *
 * The viewport is sized to the map's own aspect ratio, so the minimap rectangle *is* the map and a
 * click maps to a world point with no letterboxing to compensate for.
 */

/** Longest edge of the minimap viewport, in screen pixels. */
const MAX_PX = 176;
const MARGIN_PX = 16;
/** Below this the minimap is more clutter than orientation, so it hides itself. */
const MIN_CANVAS_PX = 640;

const FRAME_DEPTH = 100;
const WATER_COLOR = 0x0d2233;
const FRAME_COLOR = 0x9bb8c9;
const VIEW_COLOR = 0xf4f7ee;
const GATE_COLOR = 0xffffff;
const GATE_GOAL_COLOR = 0xf4f7ee;

type Viewport = { x: number; y: number; width: number; height: number; zoom: number };

function viewportFor(scene: Phaser.Scene, bounds: { width: number; height: number }): Viewport {
  const aspect = bounds.width / bounds.height;
  const width = aspect >= 1 ? MAX_PX : MAX_PX * aspect;
  const height = aspect >= 1 ? MAX_PX / aspect : MAX_PX;
  const canvas = scene.scale.gameSize;

  return {
    x: canvas.width - width - MARGIN_PX,
    y: canvas.height - height - MARGIN_PX,
    width,
    height,
    zoom: width / bounds.width,
  };
}

export class EditorMinimap {
  private readonly scene: Phaser.Scene;
  private readonly camera: Phaser.Cameras.Scene2D.Camera;
  /** Gates and the view rectangle, drawn at map scale and hidden from the main camera. */
  private readonly overlay: Phaser.GameObjects.Graphics;
  private viewport: Viewport;
  private wanted = true;
  /** Content + view signature the overlay was last painted for. Empty means nothing is drawn. */
  private drawnFor = '';

  constructor(scene: Phaser.Scene, map: MapDocument) {
    this.scene = scene;
    this.viewport = viewportFor(scene, map.bounds);

    this.camera = scene.cameras
      .add(this.viewport.x, this.viewport.y, this.viewport.width, this.viewport.height)
      .setName('minimap')
      .setZoom(this.viewport.zoom);
    this.camera.setBackgroundColor(WATER_COLOR);
    // Objects rendered through this camera must not answer pointer events, or every island
    // would have a second, tiny hit area stacked over the corner of the screen.
    this.camera.inputEnabled = false;

    this.overlay = scene.add.graphics().setDepth(FRAME_DEPTH);
    scene.cameras.main.ignore(this.overlay);

    this.layout(map);
  }

  /** True when a screen-space point is over the minimap, so the caller can treat it as a jump. */
  contains(x: number, y: number): boolean {
    return (
      this.camera.visible &&
      x >= this.viewport.x &&
      x <= this.viewport.x + this.viewport.width &&
      y >= this.viewport.y &&
      y <= this.viewport.y + this.viewport.height
    );
  }

  worldPoint(x: number, y: number): Vec2 {
    const point = this.camera.getWorldPoint(x, y);
    return { x: point.x, y: point.y };
  }

  /**
   * Identity of everything the overlay paints: the main camera's footprint plus the gates and
   * routes it strokes. Gate and route endpoints are the only geometry read, so a terrain edit
   * does not invalidate it and dragging a gate does.
   */
  private contentKey(
    map: MapDocument,
    hiddenRouteIds?: ReadonlySet<string>,
    selectedRouteIds?: ReadonlySet<string>,
  ): string {
    const view = this.scene.cameras.main.worldView;
    const parts: string[] = [
      `${Math.round(view.x)},${Math.round(view.y)},${Math.round(view.width)},${Math.round(view.height)}`,
      String(map.bounds.width),
      String(map.bounds.height),
    ];

    for (const checkpoint of map.checkpoints) {
      parts.push(
        `c${checkpoint.a.x},${checkpoint.a.y},${checkpoint.b.x},${checkpoint.b.y},${checkpoint.isGoal ? 1 : 0}`,
      );
    }

    for (const route of map.routes) {
      if (hiddenRouteIds?.has(route.id)) {
        continue;
      }

      const fill = resolveColor(route.color).fill;
      parts.push(
        `r${route.id},${route.cornerRadius},${fill},${selectedRouteIds?.has(route.id) ? 1 : 0}`,
        ...route.points.map((point) => `${point.x},${point.y}`),
      );
    }

    return parts.join('|');
  }

  /** Everything the minimap should not render. Cheap and idempotent, so it can run on every redraw. */
  hide(objects: readonly Phaser.GameObjects.GameObject[]): void {
    for (const object of objects) {
      this.camera.ignore(object);
    }
  }

  /** Author toggle. The camera still hides itself on a canvas smaller than `MIN_CANVAS_PX`. */
  setVisible(on: boolean): void {
    this.wanted = on;
    this.applyVisibility();
  }

  /** Re-derives the viewport after a map resize or a canvas resize. */
  layout(map: MapDocument): void {
    // Stroke width and the frame rect come off the viewport, so the painted overlay is stale.
    this.drawnFor = '';
    this.viewport = viewportFor(this.scene, map.bounds);
    this.camera.setViewport(
      this.viewport.x,
      this.viewport.y,
      this.viewport.width,
      this.viewport.height,
    );
    this.camera.setZoom(this.viewport.zoom);
    this.camera.centerOn(map.bounds.width / 2, map.bounds.height / 2);
    this.applyVisibility();
  }

  private applyVisibility(): void {
    const canvas = this.scene.scale.gameSize;
    const show =
      this.wanted && canvas.width >= MIN_CANVAS_PX && canvas.height >= MIN_CANVAS_PX;
    this.camera.setVisible(show);
    this.overlay.setVisible(show);
  }

  /**
   * Gates and the main camera's footprint, both stroked in world units: the shared graphics layers
   * size their strokes for the main camera's zoom, which at minimap scale rounds away to nothing.
   *
   * The scene calls this every frame because pan and zoom move the view rectangle without going
   * through `redraw()`. Graphics replays its whole command buffer per frame either way, but
   * rebuilding it costs a `lineBetween` per gate and a path per route, so an unchanged overlay
   * returns on one string compare (docs/PERFORMANCE.md, root cause 4). `force` is for a resize,
   * where the viewport changed but the key did not.
   */
  draw(
    map: MapDocument,
    options: {
      hiddenRouteIds?: ReadonlySet<string>;
      selectedRouteIds?: ReadonlySet<string>;
      force?: boolean;
    } = {},
  ): void {
    const gfx = this.overlay;

    if (!this.camera.visible) {
      if (this.drawnFor !== '') {
        this.drawnFor = '';
        gfx.clear();
      }

      return;
    }

    const key = this.contentKey(map, options.hiddenRouteIds, options.selectedRouteIds);

    if (!options.force && key === this.drawnFor) {
      return;
    }

    this.drawnFor = key;
    gfx.clear();

    const stroke = 1 / this.viewport.zoom;

    for (const checkpoint of map.checkpoints) {
      gfx.lineStyle(
        stroke * (checkpoint.isGoal ? 2.6 : 2),
        checkpoint.isGoal ? GATE_GOAL_COLOR : GATE_COLOR,
        checkpoint.isGoal ? 0.95 : 0.7,
      );
      gfx.lineBetween(checkpoint.a.x, checkpoint.a.y, checkpoint.b.x, checkpoint.b.y);
    }

    const hiddenRouteIds = options.hiddenRouteIds;
    const selectedRouteIds = options.selectedRouteIds ?? new Set<string>();

    for (const route of map.routes) {
      if (hiddenRouteIds?.has(route.id)) {
        continue;
      }
      const outline = routeOutline(route.points, route.cornerRadius);
      const first = outline[0];

      if (!first || outline.length < 2) {
        continue;
      }

      gfx.lineStyle(
        stroke * 1.6,
        resolveColor(route.color).fill,
        selectedRouteIds.has(route.id) ? 0.85 : UNSELECTED_ROUTE_ALPHA,
      );
      gfx.beginPath();
      gfx.moveTo(first.x, first.y);

      for (let i = 1; i < outline.length; i += 1) {
        const point = outline[i];

        if (point) {
          gfx.lineTo(point.x, point.y);
        }
      }

      gfx.strokePath();
    }

    const view = this.scene.cameras.main.worldView;
    gfx.lineStyle(stroke, VIEW_COLOR, 0.9);
    gfx.strokeRect(view.x, view.y, view.width, view.height);

    // Inset by half the stroke so the frame lands inside the viewport instead of being clipped.
    const half = stroke / 2;
    gfx.lineStyle(stroke, FRAME_COLOR, 0.8);
    gfx.strokeRect(half, half, map.bounds.width - stroke, map.bounds.height - stroke);
  }

  destroy(): void {
    if (this.overlay.scene) {
      this.overlay.destroy();
    }

    if (this.camera.scene) {
      this.scene.cameras.remove(this.camera, true);
    }
  }
}
